import type { NormalizedRebalancingPlan } from '@cast/types';

import type {
  AutopilotRecommendation,
  OptimizationGoal,
  EnvironmentType,
} from './open-api-types/autopilot-types';

export enum AutoOptimizationScreen {
  Priority = 'priority',
  Features = 'features',
  Exclusions = 'exclusions',
  Recommendations = 'recommendations',
  Applying = 'applying',
  Completed = 'completed',
}

// Workload optimization steps only — rebalancer runs in parallel, not as a step.
export type ApplyStep = 'create' | 'update' | 'order';

export type RebalanceApplyState = {
  status: 'loading' | 'error' | 'succeeded';
  error: string | null;
};

export type EvictorApplyState = {
  status: 'loading' | 'error' | 'succeeded';
  error: string | null;
};

export type ApplyState = {
  currentStep: ApplyStep;
  error: string | null;
  // name → created policy id. Persists across retries so already-created policies are skipped.
  createdPolicyIds: Record<string, string>;
  updatesCompleted: boolean;
  orderCompleted: boolean;
  // null = rebalancer not requested; populated when rebalancer is triggered.
  rebalance: RebalanceApplyState | null;
  // null = schedule job not requested; populated when schedule job is triggered.
  schedule: ScheduleApplyState | null;
  // null before apply starts; populated when evictor config is applied.
  evictor: EvictorApplyState | null;
  // Increments per retry. When it reaches 3, state resets to INITIAL_STATE and wizard returns to Profile.
  applyRetryAttempts: number;
};

export type RecommendationsState = {
  data: AutopilotRecommendation | null;
  isLoading: boolean;
  error: string | null;
};

export type RebalancingPlanFlowState =
  | { type: 'idle' }
  | { type: 'nodes-eligibility-check' }
  | { type: 'nodes-eligibility-check-error'; error: unknown }
  | { type: 'no-eligible-nodes' }
  | { type: 'generating-plan' }
  | { type: 'plan-generation-error'; error: unknown }
  | {
      type: 'plan-generated';
      plan: NormalizedRebalancingPlan;
      hasPositiveSavings: boolean;
    };

export type RebalancingScheduleFormState = {
  enabled: boolean;
  cron: string;
  timeZone: string;
};

export type ScheduleApplyState = {
  status: 'loading' | 'error' | 'succeeded';
  error: string | null;
};

export type AutoOptimizationWizardState = {
  preferences: {
    priority: OptimizationGoal | null;
    clusterUsage: EnvironmentType | null;
  };
  features: {
    nodeAutoscaling: boolean;
    workloadAutoscaling: boolean;
  };
  excludedNamespaces: string[];
  recommendations: RecommendationsState;
  applyState: ApplyState | null;
  currentScreen: AutoOptimizationScreen;
};

export type KentBinPackingFlowState =
  | {
      type: 'checking-crb-compatibility';
    }
  | {
      type: 'checking-crb-compatibility-error';
      error: unknown;
      retry: () => void;
    }
  | {
      type: 'checking-crb-compatibility-complete';
      isCompatible: boolean;
    };

export type BinPackingFlowState =
  | {
      mode: 'autoscaler';
    }
  | {
      mode: 'kent';
      kentBinPackingFlowState: KentBinPackingFlowState;
    };
